/* SleepHappy Homepage Rhythm + Site-wide Scroll Fade
   ===================================================
   Two concerns, one file:

   1. SECTION SEPARATION (homepage only)
      Alternating subtle background between sections so each zone feels distinct.
      Scoped to body.hdt-page-type-index — purely a homepage design choice.

   2. SCROLL-DRIVEN FADE (site-wide)
      Inner content (headings, paragraphs, cards, images, buttons) gracefully
      fades up into view as the user scrolls — on EVERY page (home, PDP,
      collection, blog, cart, articles, etc.). Boss 2026-05-23: "ตั้งแต่เริ่ม
      เลื่อนหน้าลงมา" — fade should kick in the moment scrolling begins.

   Performance + a11y:
   - IntersectionObserver, never a scroll listener (no INP cost)
   - Skips elements that are ALREADY in the initial viewport (above-fold)
     so users never see a flash of invisible content
   - One-shot reveal (observer.unobserve after fade)
   - prefers-reduced-motion → reveal-only, no transition
   - Stagger inner elements by 80ms (max 240ms) so each section reads as
     a soft wave instead of a hard slam-in
   - Inline `!important` via setProperty — strongest cascade, beats any
     upstream rule that would otherwise flip opacity to 1 immediately
*/
(function(){
  var prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(){
    var isHomepage = document.body.classList.contains('hdt-page-type-index');

    /* ============ 1. SECTION SEPARATION (homepage only) ============ */
    if (isHomepage) {
      var sections = Array.prototype.slice.call(
        document.querySelectorAll('.shopify-section[id*="template--"]')
      );
      sections.forEach(function(section, idx){
        if (idx < 2) return; /* skip hero + slideshow */
        if (idx % 2 === 0) section.classList.add('sh-bg-alt');
      });
    }

    /* ============ 2. SCROLL-DRIVEN FADE (with exclusions) ============ */
    /* Boss 2026-05-26 LATE2 (expanded): skip fade on content-dense static templates
       where animation gets in the way of reading. Covers:
         - template-page (ALL /pages/* — warranty, contact, faq, about, privacy, etc.)
         - template-cart (interactive checkout-related)
         - template-search (results scanning)
         - template-customers / template-account (forms + settings)
         - template-404 (error page)
       Fade still fires on: index (home), product (PDP), collection, blog, article. */
    var bc = document.body.classList;
    var skipTemplates = ['template-page', 'template-cart', 'template-search', 'template-customers', 'template-account', 'template-404'];
    var i;
    for (i = 0; i < skipTemplates.length; i++) {
      if (bc.contains(skipTemplates[i])) return;
    }
    /* Additional pathname-specific exclusions (paths that don't match a template above) */
    var noFadePaths = [];
    if (noFadePaths.indexOf(location.pathname) !== -1) return;
    if (prefersReducedMotion) return;

    /* Target content elements anywhere a Shopify section renders.
       Includes our custom homepage components (.sh-goodsleep, .sh-trust,
       .sh-coll, .sh-bs__card) and Halo's card / button / heading patterns.

       NOTE — we deliberately do NOT target <img> directly. Putting opacity:0
       on an <img loading="lazy"> below the fold makes Chrome flag the
       element as invisible and skip the network fetch entirely (a real
       browser optimization). The card/container fades, the <img> inside
       inherits the fade visually, and native lazy-loading still works. */
    var selector = [
      '.shopify-section h1',
      '.shopify-section h2',
      '.shopify-section h3',
      '.shopify-section h4',
      '.shopify-section p',
      '.shopify-section .hdt-card',
      '.shopify-section .hdt-card-product',
      '.shopify-section .hdt-button',
      '.shopify-section .hdt-btn',
      '.shopify-section .hdt-block-text',
      '.sh-goodsleep',
      '.sh-trust',
      '.sh-coll',
      '.sh-bs__card'
    ].join(', ');

    var viewportHeight = window.innerHeight;
    var fadeTargets = [];
    var staggerIdx = 0;
    var lastSection = null;

    document.querySelectorAll(selector).forEach(function(el){
      if (el.classList.contains('sh-reveal')) return;

      /* Boss 2026-05-26 LATE2: skip footer entirely — fade animation on
         site-wide footer hurts perceived stability (Casper/Helix pattern). */
      if (el.closest('.shopify-section-group-footer-group, footer, .hdt-footer')) return;

      /* Skip if a sh-reveal ancestor would double-fade us, OR if inside a
         horizontal carousel container (mobile swipe sections) — fading on
         horizontal scroll causes vertical jank during swipe. */
      var parent = el.parentElement;
      var skip = false;
      while (parent){
        if (parent.classList && parent.classList.contains('sh-reveal')){
          skip = true; break;
        }
        if (parent.classList && (
          parent.classList.contains('sh-bs__grid') ||
          parent.classList.contains('sh-cs__grid')
        )){
          skip = true; break;
        }
        parent = parent.parentElement;
      }
      if (skip) return;

      /* Skip elements visible in initial viewport (above-fold).
         If the element's top is within the first viewport, it's already
         visible on page load — fading it in would only show a brief
         blank flash. We only fade what arrives DURING the scroll. */
      var rect = el.getBoundingClientRect();
      var docTop = rect.top + window.scrollY;
      if (docTop < viewportHeight) return;

      /* Reset stagger when we cross into a new section so each section
         starts its own wave, not a cumulative drift across the page */
      var section = el.closest('.shopify-section');
      if (section !== lastSection){ staggerIdx = 0; lastSection = section; }

      el.classList.add('sh-reveal');
      var delay = Math.min(staggerIdx * 80, 240);
      el.style.setProperty('opacity', '0', 'important');
      el.style.setProperty('transform', 'translateY(36px)', 'important');
      el.style.setProperty(
        'transition',
        'opacity 800ms cubic-bezier(.22,.61,.36,1) ' + delay + 'ms, ' +
        'transform 800ms cubic-bezier(.22,.61,.36,1) ' + delay + 'ms',
        'important'
      );
      el.style.setProperty('will-change', 'opacity, transform', 'important');
      staggerIdx++;
      fadeTargets.push(el);
    });

    if (fadeTargets.length === 0) return;

    var observer = new IntersectionObserver(function(entries){
      entries.forEach(function(entry){
        if (entry.isIntersecting){
          entry.target.classList.add('sh-revealed');
          entry.target.style.setProperty('opacity', '1', 'important');
          entry.target.style.setProperty('transform', 'translateY(0)', 'important');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.08,
      /* Pull the trigger boundary up so reveal fires JUST before the
         element fully enters — feels proactive, never laggy */
      rootMargin: '0px 0px -6% 0px'
    });

    fadeTargets.forEach(function(el){ observer.observe(el); });

    /* ============ 3. CAROUSEL GESTURE COORDINATOR (mobile only) ============
       iOS Safari aggressively claims gestures inside overflow-x containers
       as horizontal, blocking vertical page scroll. CSS touch-action alone
       doesn't reliably win on iOS — Emma/Casper-style fix: detect direction
       in the first ~10px of motion and lock the carousel's scrollLeft if
       vertical is dominant, so the page scroll wins cleanly.

       passive: true → never blocks native scroll, zero INP cost. */
    function bindCarousel(carousel){
      if (carousel.__shGestureBound) return;
      carousel.__shGestureBound = true;
      var startX = 0, startY = 0, lockedScrollLeft = 0, axis = null;
      carousel.addEventListener('touchstart', function(e){
        var t = e.touches[0];
        startX = t.clientX;
        startY = t.clientY;
        lockedScrollLeft = carousel.scrollLeft;
        axis = null;
      }, { passive: true });
      carousel.addEventListener('touchmove', function(e){
        var t = e.touches[0];
        var dx = t.clientX - startX;
        var dy = t.clientY - startY;
        var ax = Math.abs(dx), ay = Math.abs(dy);
        /* Decide axis once motion exceeds a small threshold */
        if (!axis && (ax > 8 || ay > 8)){
          axis = (ay > ax * 1.2) ? 'v' : 'h';
        }
        /* Vertical-dominant → keep carousel pinned; page scrolls naturally */
        if (axis === 'v'){
          carousel.scrollLeft = lockedScrollLeft;
        }
      }, { passive: true });
      carousel.addEventListener('touchend', function(){ axis = null; }, { passive: true });
    }
    document.querySelectorAll('.sh-bs__grid, .sh-cs__grid').forEach(bindCarousel);

    /* ============ 4. PAGINATION DOTS (Plan C — Emma/Casper polish) ============
       Inject dots BELOW each mobile carousel + track active card via
       IntersectionObserver scoped to the carousel itself + click-to-scroll.
       CSS hides dots on desktop (≥901px) via @media; we still inject them
       so the DOM is consistent across viewport changes. */
    function setupDots(carousel){
      if (carousel.__shDotsBound) return;
      carousel.__shDotsBound = true;
      var isBS = carousel.classList.contains('sh-bs__grid');
      var dotsClass = isBS ? 'sh-bs__dots' : 'sh-cs__dots';
      var dotClass = isBS ? 'sh-bs__dot' : 'sh-cs__dot';
      var activeClass = dotClass + '--active';
      var cards = Array.prototype.slice.call(carousel.children);
      if (cards.length < 2) return;
      var dots = document.createElement('div');
      dots.className = dotsClass;
      dots.setAttribute('role', 'tablist');
      cards.forEach(function(card, idx){
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = dotClass;
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'ดูสินค้าที่ ' + (idx + 1));
        dot.addEventListener('click', function(){
          card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        });
        dots.appendChild(dot);
      });
      carousel.parentNode.insertBefore(dots, carousel.nextSibling);
      /* Set first dot active on init */
      dots.firstChild.classList.add(activeClass);
      /* IO scoped to carousel — tracks which card is "in view" inside the scroll
         container, not the page. threshold 0.55 = card mostly visible to count as active. */
      var io = new IntersectionObserver(function(entries){
        entries.forEach(function(entry){
          if (entry.isIntersecting && entry.intersectionRatio >= 0.55){
            var idx = cards.indexOf(entry.target);
            if (idx < 0) return;
            Array.prototype.forEach.call(dots.children, function(d, i){
              if (i === idx) d.classList.add(activeClass);
              else d.classList.remove(activeClass);
            });
          }
        });
      }, { root: carousel, threshold: [0.55, 0.85] });
      cards.forEach(function(c){ io.observe(c); });
    }
    document.querySelectorAll('.sh-bs__grid, .sh-cs__grid').forEach(setupDots);

    /* ============ 5. CLICKABLE CARD (Plan C+ — Boss 2026-05-23) ============
       Best Sellers card: image and CTA are already <a>, but title/sub/chips/
       price are static text — clicks there go nowhere. Casper/Emma pattern:
       extend clickzone to the whole card via JS, while preserving:
         • text selection (skip nav if user is selecting)
         • existing inner links (CTA, image link handle their own clicks)
         • keyboard a11y (tab still lands on inner links — unchanged) */
    function makeCardClickable(card){
      if (card.__shClickBound) return;
      card.__shClickBound = true;
      var cta = card.querySelector('.sh-bs__cta');
      if (!cta || !cta.href) return;
      card.style.cursor = 'pointer';
      card.addEventListener('click', function(e){
        /* Bypass if user clicked an existing link (CTA, image link, etc.) */
        if (e.target.closest('a, button')) return;
        /* Bypass if user is actively selecting text */
        var sel = window.getSelection && window.getSelection();
        if (sel && sel.toString().length > 0) return;
        /* Navigate to product page */
        window.location.href = cta.href;
      });
    }
    document.querySelectorAll('.sh-bs__card').forEach(makeCardClickable);
  }

  /* ============ SAFETY NET (2026-05-26 LATE2 — Boss critical fix) ============
     The IntersectionObserver above silently fails on some page templates
     (warranty/return-policy page reported with 67 invisible elements stuck at
     opacity:0). This fallback layer runs INDEPENDENTLY of init() so it survives
     any error in the main script. It does NOT compete with the observer —
     it only reveals elements when they actually enter viewport (same trigger,
     plus a 3s catch-all sweep). */
  function safetyReveal(){
    var nodes = document.querySelectorAll('.sh-reveal:not(.sh-revealed)');
    if (!nodes.length) return;
    var winH = window.innerHeight;
    var i = nodes.length;
    while (i--){
      var el = nodes[i];
      var r = el.getBoundingClientRect();
      if (r.top < winH + 80 && r.bottom > -80){
        el.classList.add('sh-revealed');
        el.style.setProperty('opacity', '1', 'important');
        el.style.setProperty('transform', 'translateY(0)', 'important');
      }
    }
  }
  var _shSafetyTimer;
  function _shScheduleSafety(){
    clearTimeout(_shSafetyTimer);
    _shSafetyTimer = setTimeout(safetyReveal, 80);
  }
  function bindSafetyNet(){
    /* Run initial sweep immediately (catches anything already in viewport) */
    safetyReveal();
    /* Bind passive scroll + resize listener — zero INP cost */
    window.addEventListener('scroll', _shScheduleSafety, { passive: true });
    window.addEventListener('resize', _shScheduleSafety, { passive: true });
    /* Catch-all: force-reveal anything still hidden in viewport after 3s
       (gives the main observer a chance to fire first) */
    setTimeout(safetyReveal, 3000);
  }

  if (document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', function(){ init(); bindSafetyNet(); });
  } else {
    init();
    bindSafetyNet();
  }
})();
